import moment from 'moment';
import { useEffect, useState } from 'react';
import styled, { ColorsTypes, useTheme } from 'styled-components';

import { createUrl, formatSecondsToTime, stripTags } from 'app/utils';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';
import { Button, ButtonIcon, Flex, Hr, ListInfo, Masonry, Milestone, Page, RichContent, Section2, Typography } from 'components';
import { Icon } from 'components/icon';
import { IconsTypes } from 'components/icons';
import Alert, { useAlert } from 'features/alert';
import {
  defaultContent,
  ExamsMultipleAssessmentResultsChooseTypeEnum,
  ExamsPagination,
  ExamsResultCalculationTypeEnum,
  useExams,
} from 'features/exams';
import { useNavigate, useParams } from 'react-router-dom';

const Bar = styled.div<{ color?: ColorsTypes }>`
  background: ${({ theme, color = 'dark_350' }) => theme.colors[color as ColorsTypes]};
  height: 9px;
  width: 19px;
  border-radius: 4px;
  box-shadow: 0px 0px 1px 0px ${({ theme }) => theme.colors.dark_150};
`;

const Result = () => {
  const { currentTheme } = useTheme();

  const navigate = useNavigate();
  const { examUuid, assessmentUuid } = useParams();

  const [collapse, setCollapse] = useState(true);

  const { result, examsDetails, setExamsAction } = useExams();
  const { alert, clearAlert } = useAlert();

  const content = defaultContent(examsDetails, result);

  const header: { title: string; message: string; icon: IconsTypes; color: ColorsTypes }[] = [
    { title: content?.resultsTitle, message: content?.resultsMessage, icon: 'info', color: 'color100' },
    { title: content?.approvedTitle, message: content?.approvedMessage, icon: 'checkedOutline', color: 'primary_400' },
    { title: content?.reprovedTitle, message: content?.reprovedMessage, icon: 'closeOutline', color: 'alert_400' },
  ];

  let statusResult = 0;
  const hideResult = result?.isApproved === null;
  if (!hideResult) {
    statusResult = result?.isApproved ? 1 : 2;
  }

  const startDate = moment(result?.startDate);
  const finalizeDate = moment(result?.finalizeDate);

  const isResultCalculationPercentual = result.resultCalculationType === ExamsResultCalculationTypeEnum.Percentual;

  let resultLabel = '-';
  if (!hideResult) {
    if (isResultCalculationPercentual) {
      resultLabel = `${result?.rightPercentual}%`;
    } else {
      resultLabel = `${Number(result?.result)} / ${result?.maximumResult} - ${result?.rightPercentual}%`;
    }
  }

  // let examPointsPercentage = '0';
  // if (!hideResult && result?.result && result?.maximumResult) {
  //   examPointsPercentage = ((result?.result / result?.maximumResult) * 100).toFixed(2).replace('.00', '');
  // }

  // let examQuestionsPercentage = '0';
  // if (!hideResult && result?.rightQuestionAnswerCount && result?.questionCount) {
  //   examQuestionsPercentage = ((result?.rightQuestionAnswerCount / result?.questionCount) * 100).toFixed(2).replace('.00', '');
  // }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    return () => {
      clearAlert();
    };
  }, []);

  const hasInvalidQuestions = result?.subject?.reduce((subjectPrev, subject) => {
    if (subjectPrev) return true;

    return subject.question.reduce((questionPrev, question) => {
      if (questionPrev) return true;

      return question.isQuestionInvalidated;
    }, false);
  }, false);

  return (
    <Page title="Resultado da avaliação">
      <ExamsPagination />
      <Flex flexGrow={0} my={{ m: 30, t: 50, d: 60 }} flexDirection="column">
        <Section2 flexWrap={{ m: 'wrap', d: 'nowrap' }}>
          <Flex mr={{ d: 80 }} alignItems="flex-start" flexWrap="nowrap">
            {/* <Icon icon={header[statusResult].icon} size="large" color={header[statusResult].color} /> */}

            <Icon icon={header[statusResult].icon} size="large" data-color={header[statusResult].color} />

            <Flex flexDirection="column" alignItems="flex-start" ml={{ m: 20, d: 32 }}>
              <Typography type="type3" color="gray_500" upper>
                Resultado
              </Typography>

              <Typography type="type6" color="color100" fontWeight="bold" mt={1}>
                {header[statusResult].title}
              </Typography>

              {!!header[statusResult].message && (
                <Flex mt={4}>
                  <RichContent defaultType="type2" currentTheme={currentTheme}>
                    {header[statusResult].message}
                  </RichContent>
                </Flex>
              )}

              {!!(alert?.type && alert?.message) && (
                <Flex mt={4}>
                  <Alert py={2} px={3} />
                </Flex>
              )}

              {!!result?.allowStartPracticeExamCount && (
                <Flex
                  mt={5}
                  alignItems={{ m: 'flex-start', d: 'center' }}
                  flexDirection={{ m: 'column', d: 'row' }}
                  flexWrap={{ m: 'wrap', d: 'nowrap' }}
                >
                  <Button onClick={() => setExamsAction('start')} square color="dark_350">
                    Refazer avaliação
                  </Button>

                  <Typography type="type0" ml={{ m: 0, d: 3 }} mt={{ m: 3, d: 0 }} color="color80" lineHeight={1.3}>
                    Você pode refazer sua avaliação mais {result?.allowStartPracticeExamCount}{' '}
                    {result?.allowStartPracticeExamCount === 1 ? `vez` : `vezes`}.
                    <br />
                    {result?.multipleAssessmentResultsChooseType === ExamsMultipleAssessmentResultsChooseTypeEnum.BestResults
                      ? 'Sua nota será definida por sua maior pontuação.'
                      : 'Sua nota será substituida pela nova pontuação.'}
                  </Typography>
                </Flex>
              )}
            </Flex>
          </Flex>

          <Flex flexGrow={0} width={{ m: '100%', t: 310 }} flexShrink={0} mt={{ m: 30, d: 0 }}>
            <ListInfo
              list={[
                { label: 'Realizado em', title: formatDateFromUtcToTimezone(result?.finalizeDate).format('DD/MM/YYYY HH:MM'), icon: 'calendar' },

                { label: 'Tempo de prova', title: formatSecondsToTime(finalizeDate.diff(startDate, 'seconds')), icon: 'clockTimeFourOutline' },

                {
                  label: 'Resultado',
                  title: resultLabel,
                  icon: 'checkboxMarkedCircleOutline',
                },

                // {
                //   label: 'pontos certos',
                //   title: `${hideResult ? '—' : `${result ? Number(result?.result) : ''}`} / ${result?.maximumResult} - ${examPointsPercentage}%`,
                //   icon: 'checkedOutline',
                //   show: result?.questionCount !== result?.maximumResult,
                // },
              ]}
            />
          </Flex>
        </Section2>

        <Section2 mt={6} mb={2}>
          <Hr color="articleLine" m={0} />

          <Flex justifyContent="space-between" flexWrap="nowrap" alignItems="center" my={3}>
            <Typography type="type5" color="color100" fontWeight="medium" mt={1}>
              Respostas por assunto
            </Typography>

            <Flex flexGrow={0} flexDirection={{ m: 'column', d: 'row' }}>
              <Flex alignItems="center" flexWrap="nowrap">
                <Bar color="primary_400" />
                <Typography ml={2} type="type0" color="gray_500" style={{ whiteSpace: 'nowrap' }}>
                  Atingido
                </Typography>
              </Flex>

              <Flex ml={{ m: 0, d: 3 }} alignItems="center" flexWrap="nowrap">
                <Bar color="white" />
                <Typography ml={2} type="type0" color="gray_500" style={{ whiteSpace: 'nowrap' }}>
                  Objetivo
                </Typography>
              </Flex>

              <Flex ml={{ m: 0, d: 3 }} alignItems="center" flexWrap="nowrap">
                <Bar color="alert_400" />
                <Typography ml={2} type="type0" color="gray_500" style={{ whiteSpace: 'nowrap' }}>
                  Não atingido
                </Typography>
              </Flex>

              {hasInvalidQuestions && (
                <Flex ml={{ m: 0, d: 3 }} alignItems="center" flexWrap="nowrap">
                  <Bar color="warning_400" />
                  <Typography ml={2} type="type0" color="gray_500" style={{ whiteSpace: 'nowrap' }}>
                    Invalidada
                  </Typography>
                </Flex>
              )}

              <ButtonIcon
                icon={collapse ? 'chevronUp' : 'chevronDown'}
                label={collapse ? 'Fechar todos' : 'Abrir todos'}
                onClick={() => setCollapse(!collapse)}
                iconPosition="after"
                ml={4}
                bg="transparent"
                color="primary_400"
                colorHover="primary_200"
                display={{ m: 'none', d: 'flex' }}
              />
            </Flex>
          </Flex>
        </Section2>

        <Section2>
          <Masonry columns={3} spacing={2}>
            {result?.subject.map((subject, subjectIndex) => {
              // let subjectPercentage = 0;
              // if (!hideResult && subject?.result && subject?.maximumResult) {
              //   subjectPercentage = (subject?.result / subject?.maximumResult) * 100;
              // }

              let subjectTargetPercentage = 0;
              if (!hideResult && subject?.minimalResult && subject?.maximumResult) {
                subjectTargetPercentage = (subject?.minimalResult / subject?.maximumResult) * 100;
              }

              let subjectProgressLabel = '';

              if (!hideResult) {
                if (isResultCalculationPercentual) {
                  subjectProgressLabel = `${subject.rightPercentual}%`;
                } else {
                  subjectProgressLabel = `${subject?.rightPointsCount || 0} / ${subject?.totalPointsCount} - ${subject.rightPercentual}%`;
                }
              }

              return (
                <Milestone
                  key={'milestone' + subjectIndex}
                  hat={`QUESTÕES • ${subject?.questionCount} / ${result?.questionCount}`}
                  title={subject?.practiceExamSubject?.name}
                  progressLabel={subjectProgressLabel}
                  progressBarPercentage={subject.rightPercentual}
                  progressBarPercentageColor={subject?.isApproved ? 'primary_400' : 'alert_400'}
                  progressBulletPercentage={subjectTargetPercentage}
                  stones={subject?.question?.map((question) => ({
                    id: question.questionExternalId,
                    content: stripTags(question.questionText),
                    color: question.isQuestionInvalidated
                      ? 'warning_400'
                      : question.questionAnswerChoiceUuid === question.rightQuestionAnswerChoiceUuid
                      ? 'primary_400'
                      : 'alert_400',
                    isQuestionInvalidated: question.isQuestionInvalidated,
                    onClick: () => {
                      navigate(createUrl('exams/question', [examUuid, assessmentUuid, question?.uuid]));
                    },
                  }))}
                  collapse={collapse}
                />
              );
            })}
          </Masonry>
        </Section2>
      </Flex>
    </Page>
  );
};

export default Result;
